import React, { useContext, createContext, useState, ReactChild } from 'react'

export type NotificationTypes = 'SUCCESS' | 'ERROR' | 'WARNING'

export interface NotificationObject {
  type: NotificationTypes
  message: React.ReactChild
}

interface NotificationProviderContext {
  notification: NotificationObject
  setError: (error: React.ReactChild) => void
  setSuccess: (message: React.ReactChild) => void
  setWarning: (message: ReactChild) => void
  resetNotification: () => void
}

const initialNotification: NotificationObject = {
  type: 'ERROR',
  message: '',
}

const initialValues: NotificationProviderContext = {
  notification: initialNotification,
  setError: () => {},
  setSuccess: () => {},
  setWarning: () => {},
  resetNotification: () => {},
}

const NotificationContext = createContext<NotificationProviderContext>(
  initialValues
)

const NotificationProvider: React.FC = ({ children }) => {
  const [notification, setNotification] = useState<NotificationObject>(
    initialNotification
  )

  const resetNotification = () => {
    setNotification(initialNotification)
  }

  const setError = (error: React.ReactChild) => {
    setNotification({
      type: 'ERROR',
      message: error,
    })
  }

  const setSuccess = (message: React.ReactChild) => {
    setNotification({
      type: 'SUCCESS',
      message: message,
    })
  }

  const setWarning = (message: ReactChild) => {
    setNotification({
      type: 'WARNING',
      message: message,
    })
  }

  return (
    <NotificationContext.Provider
      value={{
        notification,
        setError,
        setSuccess,
        setWarning,
        resetNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotification = () => useContext(NotificationContext)
export default NotificationProvider
